import { Settings } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Card } from "@/components/ui";
import { debugLog } from "@/domains/logging/utils/debugLog";

import { ImportTab, TestTab, ImportHistoryTab, DashboardTab, SecuritiesTab } from "./tabs";

// Tab navigation configuration
const tabs = [
  { id: "import", label: "Import", path: "/investments/parqet" },
  { id: "test", label: "Test", path: "/investments/parqet/test" },
  {
    id: "dashboard",
    label: "Dashboard",
    path: "/investments/parqet/dashboard",
  },
  { id: "history", label: "History", path: "/investments/parqet/history" },
  {
    id: "securities",
    label: "Securities",
    path: "/investments/parqet/securities",
  },
];

/**
 * ParqetApp - Investment Portfolio Tracking with Tab Navigation
 *
 * Features:
 * - Import Tab: Future portfolio import functionality
 * - Test Tab: Current PDF parser testing and debugging
 *
 * @returns {JSX.Element} The ParqetApp component with tab navigation
 *
 */
export const ParqetApp = () => {
  debugLog("info", "investments", "ParqetApp component started", {
    module: "parqet",
  });

  const navigate: ReturnType<typeof useNavigate> = useNavigate();
  const [currentTab, setCurrentTab] = useState(tabs[0]);

  // Tab click handler
  const handleTabClick = (tab: (typeof tabs)[0]) => {
    setCurrentTab(tab);
    debugLog("info", "investments", "Tab switched", {
      from: currentTab?.id ?? "",
      module: "parqet",
      to: tab.id,
    });
  };

  // Render current tab content
  const renderTabContent = () => {
    switch (currentTab?.id) {
      case "import": {
        return <ImportTab />;
      }
      case "test": {
        return <TestTab />;
      }
      case "dashboard": {
        return <DashboardTab />;
      }
      case "history": {
        return <ImportHistoryTab />;
      }
      case "securities": {
        return <SecuritiesTab />;
      }
      default: {
        return <ImportTab />;
      }
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Tab Navigation */}
      <Card
        className="bg-slate-700/30 border-slate-600/50 shadow-tracq-card mb-6 flex-shrink-0"
        padding="sm"
        variant="elevated"
      >
        <div className="flex items-center justify-between">
          <div className="flex space-x-1">
            {tabs.map((tab) => (
              <button
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  currentTab?.id === tab.id
                    ? "bg-tracq-bright-blue/20 text-tracq-bright-blue border border-tracq-bright-blue/30"
                    : "text-slate-400 hover:text-white hover:bg-slate-600/30"
                }`}
                key={tab.id}
                onClick={() => {
                  handleTabClick(tab);
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <button
            className="p-2 text-slate-400 hover:text-tracq-bright-blue hover:bg-slate-600/30 rounded-lg transition"
            onClick={() => {
              const navResult = navigate("/settings/parqet");
              if (navResult instanceof Promise) {
                navResult.catch(() => {
                  // Navigation failed - ignore
                });
              }
            }}
            title="Parqet Settings"
          >
            <Settings className="w-5 h-5" />
          </button>
        </div>
      </Card>

      {/* Tab Content */}
      <div
        className="flex-1 overflow-y-auto parqet-scrollbar"
        style={{
          scrollbarColor: "#475569 #1e293b",
          scrollbarWidth: "thin",
        }}
      >
        <style>{`
          .parqet-scrollbar::-webkit-scrollbar {
            width: 8px;
          }
          .parqet-scrollbar::-webkit-scrollbar-track {
            background: #1e293b;
            border-radius: 4px;
          }
          .parqet-scrollbar::-webkit-scrollbar-thumb {
            background: #475569;
            border-radius: 4px;
          }
          .parqet-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #64748b;
          }
        `}</style>
        {renderTabContent()}
      </div>
    </div>
  );
};
